import Phaser from 'phaser';
import {
  createRng, createRun, step, resolveDraft, checksumRun, generateDraft, weaponDef,
  TICKS_POR_SEGUNDO, type EnemySim, type HeroInput, type HeroSim, type RunState, type SimConfig,
} from '@rabbit2/shared/sim';
import { clearOverlay, showDeath, showDraft, showVictory } from '../ui/overlay.ts';
import { inversionMode, reducedMotion, shakeCamera } from '../ui/motion.ts';
import { weaponName } from '../ui/labels.ts';
import { acordarAudio, iniciarMusica, sfx } from '../audio.ts';

// cor por arma (VFX). Sem def → magenta.
const WEAPON_COR: Record<string, number> = {
  bola_de_fogo: 0xff8a3d, floco_de_neve: 0xbfd7ea, estrelinhas: 0xffd166, bumerangue: 0xd9a066,
  lanca_de_cenoura: 0xff7043, torreta_de_lata: 0xb0bec5, bastao_de_bolhas: 0x7fd4ff, faca_lunar: 0xe0e0ff,
  piao_peao: 0xffab91, apito_de_passaros: 0x9ccc65, livro_de_historinhas: 0xce93d8, lampiao_de_vagalumes: 0xfff176,
};
interface Proj { x: number; y: number; vx: number; vy: number; life: number; max: number; cor: number; r: number }
interface Flash { x: number; y: number; r: number; life: number; max: number; cor: number; anel: boolean }
const SPRITE_FAMILIA = new Set(['sonequito', 'lencolzinho', 'abobrinha']); // famílias com sprite próprio
import { addLuar } from '../save.ts';

const COR_GEMA: Record<string, number> = { comum: 0x9aa5b1, rara: 0x4fc3f7, epica: 0xb388ff };
// paleta por capítulo (tint dos inimigos) — variedade visual sem mexer na sim
const CHAPTER_TINT = [
  0xffffff, 0xffffff, 0xd6ffd9, 0xffe6c2, 0xd9e2ff, // cap.1–4
  0xffd6ec, 0xd6fff4, 0xfff0c2, 0xe4d6ff, // 5–8
  0xd6f0ff, 0xffd9c2, 0xe8ffd6, 0xf0d6ff, // 9–12
];

/** RunScene — solo M1: roda a sim determinística localmente e a desenha (Phaser = só renderer, A2). */
export class RunScene extends Phaser.Scene {
  #state!: RunState;
  #config!: SimConfig;
  #acc = 0;
  #speed = 1;
  #ents!: Phaser.GameObjects.Graphics;
  #vfx!: Phaser.GameObjects.Graphics; // projéteis/efeitos de arma
  #bars!: Phaser.GameObjects.Graphics; // HP/especial (fixo na câmera)
  #bossBar!: Phaser.GameObjects.Graphics;
  #hero!: Phaser.GameObjects.Image;
  #enemyImgs = new Map<number, Phaser.GameObjects.Image>(); // pool de sprites de inimigo
  #hud!: Phaser.GameObjects.Text;
  #projs: Proj[] = [];
  #flashes: Flash[] = [];
  #enemyHp = new Map<number, { hp: number; x: number; y: number; fam: string }>(); // detecta dano/morte
  #prevGems = 0; // gemas coletadas (p/ faísca)
  #emitAcc = 0; // cadência de emissão de projéteis (cosmético)
  #orbit = 0; // ângulo das armas orbitais
  #prevSpecial = 0;
  #prevAtkCd = 0;
  #prevLevel = 1;
  #prevKills = 0;
  #faceX = 1; // direção do herói p/ flip do sprite
  #keys!: Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key>;
  #touch: HeroInput | null = null;
  #draftShown = false;
  #ended = false;
  #prevHp = Infinity; // p/ detectar dano e tremer a câmera (ACC-4 respeita reduzir-movimento)

  constructor() {
    super('run');
  }

  init(data: { seed?: number; speed?: number; chapter?: number }): void {
    const seed = data.seed ?? 1;
    this.#speed = data.speed ?? 1;
    const chapter = Math.max(1, Math.min(12, data.chapter ?? 1));
    const norno = new URLSearchParams(location.search).get('norno') === '1'; // boss final jogável (?norno=1)
    this.#config = { seed, chapter, playerCount: 1, arenaW: 1920, arenaH: 1088, ...(norno ? { norno: true } : {}) };
    this.#state = createRun(this.#config);
  }

  create(): void {
    clearOverlay(); // limpa overlay da Vila ao (re)entrar numa run
    this.#draftShown = false;
    this.#ended = false;
    this.cameras.main.setBackgroundColor('#1A1633');
    this.cameras.main.setBounds(0, 0, this.#config.arenaW, this.#config.arenaH);

    this.#pintarCenario(); // fundo noturno: gramado, estrelas e a lua (Moonlit Storybook)

    this.#ents = this.add.graphics().setDepth(4); // gemas
    this.#vfx = this.add.graphics().setDepth(8); // sob o herói (depth 10), sobre os inimigos
    this.#bossBar = this.add.graphics().setScrollFactor(0).setDepth(100);
    this.#bars = this.add.graphics().setScrollFactor(0).setDepth(100);
    this.#projs = [];
    this.#flashes = [];
    this.#enemyHp.clear();
    this.#prevGems = 0;
    for (const img of this.#enemyImgs.values()) img.destroy();
    this.#enemyImgs.clear();

    this.#hero = this.add.image(0, 0, 'pip').setDepth(10).setDisplaySize(46, 52);

    const cam = this.cameras.main;
    cam.startFollow(this.#hero, true, 0.15, 0.15);
    cam.setDeadzone(64, 48);

    // Palhaço Terrível observa nos caps 3/5/7 (AC-M4-02)
    if (this.#state.palhaco && this.textures.exists('palhaco')) {
      this.add.image(892, 92, 'palhaco').setScrollFactor(0).setDepth(99).setDisplaySize(60, 68).setAlpha(0.95);
      this.add.text(892, 134, 'Palhaço te observa…', {
        fontFamily: 'monospace', fontSize: '12px', color: '#ff9db3',
      }).setOrigin(0.5).setScrollFactor(0).setDepth(99);
    }

    this.#hud = this.add.text(12, 10, '', {
      fontFamily: 'monospace', fontSize: '16px', color: '#FFF3E0',
    }).setScrollFactor(0).setDepth(100);

    const kb = this.input.keyboard;
    if (kb) {
      this.#keys = {
        up: kb.addKey('W'), down: kb.addKey('S'), left: kb.addKey('A'), right: kb.addKey('D'),
      };
      kb.addKeys({ up2: 'UP', down2: 'DOWN', left2: 'LEFT', right2: 'RIGHT' });
    }
    this.installTestApi();
    this.#prevLevel = this.#state.heroes[0]?.level ?? 1;
    this.#prevKills = 0;

    // acorda o áudio + música no 1º gesto (política de autoplay)
    const acordar = (): void => { acordarAudio(); iniciarMusica(); };
    this.input.once('pointerdown', acordar);
    this.input.keyboard?.once('keydown', acordar);

    // marca pronto após o 1º render
    this.time.delayedCall(0, () => this.game.canvas.setAttribute('data-ready', 'true'));
  }

  #input(): HeroInput {
    const kb = this.input.keyboard;
    const down = (code: string): boolean => kb?.addKey(code).isDown ?? false; // .isDown = contínuo (segurar)
    const special = down('SPACE') || this.input.activePointer?.isDown === true; // especial: espaço ou clique
    if (this.#touch) return { ...this.#touch, special: this.#touch.special || special };
    let mx = 0;
    let my = 0;
    if (this.#keys) {
      if (this.#keys.left.isDown || down('LEFT')) mx -= 1;
      if (this.#keys.right.isDown || down('RIGHT')) mx += 1;
      if (this.#keys.up.isDown || down('UP')) my -= 1;
      if (this.#keys.down.isDown || down('DOWN')) my += 1;
    }
    return { mx, my, special };
  }

  override update(_t: number, dtMs: number): void {
    const ph0: string = this.#state.phase;
    if (ph0 === 'vitoria' || ph0 === 'derrota') {
      if (!this.#ended) this.endRun(ph0 === 'vitoria');
      this.draw();
      return;
    }
    // draft pendente → pausa a sim e mostra as cartas (1×)
    if (this.#state.pendingDraft) {
      if (!this.#draftShown) {
        this.#draftShown = true;
        showDraft(this.#state.pendingDraft.cards, (card) => {
          resolveDraft(this.#state, card);
          clearOverlay();
          this.#draftShown = false;
        });
      }
      this.draw();
      return;
    }
    const mxAtual = this.#input().mx; // direção p/ o flip do sprite
    if (mxAtual < -0.1) this.#faceX = -1;
    else if (mxAtual > 0.1) this.#faceX = 1;
    // passo fixo de 20Hz (acumulador), acelerado por ?speed=; PARA se abrir draft ou terminar
    this.#acc += (dtMs / 1000) * this.#speed;
    const passo = 1 / TICKS_POR_SEGUNDO;
    let guard = 0;
    while (this.#acc >= passo && guard < 600) {
      step(this.#state, this.#config, [this.#input()], { interactive: true });
      this.#acc -= passo;
      guard += 1;
      const ph: string = this.#state.phase;
      if (this.#state.pendingDraft || ph === 'derrota' || ph === 'vitoria') break;
    }
    // juice: tremor ao tomar dano — respeita "reduzir movimento" (ACC-4)
    const hero = this.#state.heroes[0];
    if (hero) {
      if (hero.hp < this.#prevHp - 0.5) shakeCamera(this);
      this.#prevHp = hero.hp;
    }
    if (hero && hero.level > this.#prevLevel) { sfx('level'); this.#prevLevel = hero.level; }
    this.#stepVfx(Math.min(50, dtMs), hero);
    this.draw();
  }

  private endRun(vitoria: boolean): void {
    this.#ended = true;
    const luar = this.#state.stats.luar;
    const total = addLuar(luar);
    const irVila = (): void => {
      clearOverlay();
      this.scene.start('vila', { total });
    };
    sfx(vitoria ? 'win' : 'lose');
    if (vitoria) showVictory(luar, irVila);
    else showDeath(luar, irVila);
  }

  private draw(): void {
    const h = this.#state.heroes[0];
    const t = this.#state.tick;
    if (h) {
      const bob = Math.sin(t * 0.45) * 2.6; // pulinho de coelho (determinístico via tick)
      const frame = t % 8 < 4 ? 'pip' : 'pip2'; // animação frame-a-frame (2 quadros)
      if (this.#hero.texture.key !== frame) this.#hero.setTexture(frame).setDisplaySize(46, 52);
      this.#hero.setPosition(h.x, h.y + bob).setAngle(Math.sin(t * 0.45) * 3).setFlipX(this.#faceX < 0);
      this.#hero.setAlpha(h.iframeCd > 0 && t % 4 < 2 ? 0.55 : 1); // pisca nos i-frames
    }

    const tintCap = CHAPTER_TINT[this.#config.chapter] ?? 0xffffff;
    const ato = Math.floor((this.#config.chapter - 1) / 4); // 0 cemitério · 1 fábrica · 2 deserto
    const bossKey = this.#state.norno && this.textures.exists('norno') ? 'norno' : 'golemita';
    // inimigos como SPRITES animados (pool reutilizável por id)
    const g = this.#ents;
    g.clear();
    let boss: EnemySim | null = null;
    const usados = new Set<number>();
    for (const e of this.#state.enemies) {
      if (!e.alive) continue;
      if (e.boss) boss = e;
      usados.add(e.id);
      let key = e.boss ? bossKey : (SPRITE_FAMILIA.has(e.family) ? e.family : 'sonequito');
      if (!e.boss) { // skin por ato: robô na fábrica, escaravelho no deserto
        if (ato === 1 && e.family === 'lencolzinho') key = 'robo';
        else if (ato === 2 && e.family === 'abobrinha') key = 'escaravelho';
      }
      const tam = e.boss ? 82 : e.elite ? 44 : 30;
      let img = this.#enemyImgs.get(e.id);
      if (!img) {
        img = this.add.image(e.x, e.y, key).setDepth(e.boss ? 7 : 6);
        this.#enemyImgs.set(e.id, img);
      }
      if (img.texture.key !== key) img.setTexture(key);
      if (img.getData('tam') !== tam) { img.setDisplaySize(tam, tam); img.setData('tam', tam); }
      const ph = e.phase * 6.283;
      const bob = Math.sin(t * 0.3 + ph) * (e.family === 'sonequito' ? 3 : 2);
      img.setPosition(e.x, e.y + bob).setVisible(true);
      img.setRotation(
        e.boss ? 0 : e.family === 'abobrinha' ? (e.x + e.y) * 0.02 : Math.sin(t * 0.2 + ph) * 0.12,
      );
      img.setTint(e.elite ? 0xff9a9a : tintCap); // elites avermelhados; senão paleta do capítulo
    }
    for (const [id, img] of this.#enemyImgs) {
      if (!usados.has(id)) { img.destroy(); this.#enemyImgs.delete(id); }
    }
    // gemas: diamantes com brilho
    for (const gem of this.#state.gems) {
      if (!gem.alive) continue;
      const cor = COR_GEMA[gem.tier] ?? 0x9aa5b1;
      const x = gem.x;
      const y = gem.y;
      g.fillStyle(cor, 0.28).fillCircle(x, y, 8); // brilho
      g.fillStyle(cor, 1);
      g.fillTriangle(x, y - 6, x + 4, y, x - 4, y); // metade de cima do diamante
      g.fillTriangle(x - 4, y, x + 4, y, x, y + 6); // metade de baixo
    }
    this.#drawVfx(h); // projéteis/efeitos de arma sobre os inimigos

    // barra de vida do Guardião (topo, fixa na câmera)
    this.#bossBar.clear();
    if (boss && boss.maxHp > 0) {
      const frac = Math.max(0, boss.hp / boss.maxHp);
      this.#bossBar.fillStyle(0x2b2440, 1).fillRect(230, 20, 500, 14);
      this.#bossBar.fillStyle(0xff5d8f, 1).fillRect(232, 22, 496 * frac, 10);
    }

    const s = this.#state;
    // barras de HP (vermelho) e especial (lua) — fixas na câmera
    this.#bars.clear();
    if (h) {
      const hpFrac = Math.max(0, h.hp / h.maxHp);
      this.#bars.fillStyle(0x2b2440, 1).fillRect(12, 64, 220, 16);
      this.#bars.fillStyle(0xff5d8f, 1).fillRect(14, 66, 216 * hpFrac, 12);
      const spFrac = Math.max(0, Math.min(1, h.special / 100));
      this.#bars.fillStyle(0x2b2440, 1).fillRect(12, 84, 220, 12);
      this.#bars.fillStyle(spFrac >= 1 ? 0xffd166 : 0xbfd7ea, 1).fillRect(14, 86, 216 * spFrac, 8);
    }
    const min = Math.floor(s.tick / TICKS_POR_SEGUNDO / 60);
    const seg = Math.floor(s.tick / TICKS_POR_SEGUNDO) % 60;
    const hp = h ? Math.ceil(h.hp) : 0;
    const fase = { aquecimento: '⚔', hordas: '⚔', badalada: '🌗', guardiao: '🔔', vitoria: '★', derrota: '☾' }[s.phase];
    const armas = h ? h.weapons.map((w) => `${weaponName(w.id)}${w.evolved ? '✦' : ' n' + w.level}`).join(' · ') : '';
    this.#hud.setText(
      `${fase} ${String(min).padStart(2, '0')}:${String(seg).padStart(2, '0')}   ` +
      `❤ ${hp}/${h?.maxHp ?? 0}   Nv ${h?.level ?? 0}   🌙 ${s.stats.luar}   ⚔ ${s.stats.kills}   ` +
      `inimigos ${s.enemies.reduce((n, e) => (e.alive ? n + 1 : n), 0)}\n` +
      `armas: ${armas || '—'}` +
      (h && h.special >= 100 ? '   [ESPAÇO/clique = especial pronto!]' : '') +
      (s.phase === 'derrota' ? '\n— os Sonecos te ninaram 💤' : ''),
    );
  }

  /** cenário noturno por ATO: 1 Cemitério · 2 Fábrica de Brinquedos · 3 Deserto de Areia-Estrela. */
  #pintarCenario(): void {
    const W = this.#config.arenaW;
    const H = this.#config.arenaH;
    const ato = Math.min(2, Math.floor((this.#config.chapter - 1) / 4)); // 0,1,2
    const rng = createRng(this.#config.seed ^ 0x9e37, 3);
    const r = (): number => rng.next01();
    const bg = this.add.graphics().setDepth(0);
    const dec = this.add.graphics().setDepth(1);

    const BASE = [0x1e1a3a, 0x2a231e, 0x2e2540][ato]!; // roxo / aço-marrom / areia-violeta
    const MANCHA = [
      [0x241f47, 0x2a2350], [0x362b22, 0x40332a], [0x3a2f52, 0x463a63],
    ][ato]!;
    bg.fillStyle(BASE, 1).fillRect(0, 0, W, H);
    for (let i = 0; i < 90; i++) {
      bg.fillStyle(r() < 0.5 ? MANCHA[0]! : MANCHA[1]!, 0.6).fillCircle(r() * W, r() * H, 40 + r() * 90);
    }

    if (ato === 0) {
      for (let i = 0; i < 70; i++) { const x = r() * W; const y = r() * H; dec.fillStyle(0x3a3466, 0.7).fillTriangle(x - 4, y + 4, x, y - 7, x + 4, y + 4); } // grama
      for (let i = 0; i < 22; i++) { // lápides
        const x = r() * W; const y = r() * H;
        dec.fillStyle(0x4a4466, 0.8).fillRoundedRect(x - 9, y - 22, 18, 24, 8);
        dec.fillStyle(0x2a2350, 0.9).fillRect(x - 2, y - 16, 4, 12).fillRect(x - 6, y - 12, 12, 4);
      }
    } else if (ato === 1) {
      for (let i = 0; i < 16; i++) { // engrenagens
        const x = r() * W; const y = r() * H; const rad = 14 + r() * 26;
        dec.lineStyle(4, 0x5a4a36, 0.5).strokeCircle(x, y, rad);
        dec.fillStyle(0x4a3d2a, 0.4).fillCircle(x, y, rad * 0.5);
      }
      for (let i = 0; i < 14; i++) { const x = r() * W; const y = r() * H; dec.fillStyle(0x3a2f22, 0.6).fillRect(x, y, 14, 90 + r() * 120); } // canos
      for (let i = 0; i < 10; i++) dec.fillStyle(0xff8a3d, 0.12).fillCircle(r() * W, r() * H, 60); // fornalhas
    } else {
      for (let i = 0; i < 10; i++) { const x = r() * W; const y = r() * H; dec.fillStyle(0x463a63, 0.5).fillEllipse(x, y, 260 + r() * 260, 90 + r() * 70); } // dunas
    }

    const stars = this.add.graphics().setDepth(1);
    const nEstrelas = ato === 1 ? 80 : ato === 2 ? 260 : 170; // deserto = areia-estrela; fábrica = interior
    for (let i = 0; i < nEstrelas; i++) {
      const grande = r() < 0.14;
      stars.fillStyle(grande ? 0xfff176 : 0xfff3c4, 0.25 + r() * 0.55);
      stars.fillCircle(r() * W, r() * H, grande ? 2.4 : 1.1);
    }
    const luaTam = ato === 2 ? 210 : 160;
    this.add.image(800, 120, 'moon').setScrollFactor(0.06).setDepth(1).setAlpha(0.92).setDisplaySize(luaTam, luaTam);
  }

  #nearest(hx: number, hy: number): EnemySim | null {
    let best: EnemySim | null = null;
    let bd = Infinity;
    for (const e of this.#state.enemies) {
      if (!e.alive) continue;
      const d = (e.x - hx) * (e.x - hx) + (e.y - hy) * (e.y - hy);
      if (d < bd) { bd = d; best = e; }
    }
    return best;
  }

  /** avança/emite VFX de arma (cosmético — não toca a sim). */
  #stepVfx(dtMs: number, hero: HeroSim | undefined): void {
    const dt = dtMs / 1000;
    this.#orbit = (this.#orbit + dt * 2.4) % (Math.PI * 2);

    // projéteis: avança e some
    for (const p of this.#projs) { p.x += p.vx * dt; p.y += p.vy * dt; p.life -= dt; }
    this.#projs = this.#projs.filter((p) => p.life > 0);
    for (const f of this.#flashes) { f.life -= dt; if (f.anel) f.r += dt * 90; }
    this.#flashes = this.#flashes.filter((f) => f.life > 0);

    // detecta dano/morte dos inimigos → flash de dano / poof de morte
    const vivos = new Set<number>();
    for (const e of this.#state.enemies) {
      if (!e.alive || e.boss) continue;
      vivos.add(e.id);
      const antes = this.#enemyHp.get(e.id);
      if (antes && e.hp < antes.hp - 0.1) {
        this.#flashes.push({ x: e.x, y: e.y, r: 10, life: 0.12, max: 0.12, cor: 0xffffff, anel: false });
      }
      this.#enemyHp.set(e.id, { hp: e.hp, x: e.x, y: e.y, fam: e.family });
    }
    for (const [id, info] of this.#enemyHp) {
      if (!vivos.has(id)) {
        // poof de morte na última posição (2 partículas)
        const cor = info.fam === 'abobrinha' ? 0xff8a3d : info.fam === 'sonequito' ? 0xbfd7ea : 0xfff3e0;
        this.#flashes.push({ x: info.x, y: info.y, r: 8, life: 0.28, max: 0.28, cor, anel: true });
        this.#flashes.push({ x: info.x, y: info.y, r: 6, life: 0.2, max: 0.2, cor: 0xffffff, anel: false });
        this.#enemyHp.delete(id);
      }
    }

    if (this.#state.stats.kills > this.#prevKills) { sfx('hit'); this.#prevKills = this.#state.stats.kills; }

    if (!hero || !hero.alive) return;
    // faísca + som ao coletar gema
    if (this.#state.stats.gemsColetadas > this.#prevGems) {
      for (let k = 0; k < 3; k++) {
        this.#flashes.push({ x: hero.x + (k - 1) * 6, y: hero.y - 6, r: 3, life: 0.3, max: 0.3, cor: 0x9fe0ff, anel: false });
      }
      sfx('gem');
    }
    this.#prevGems = this.#state.stats.gemsColetadas;
    // especial disparou (carga caiu de cheia p/ ~0) → anel grande
    if (this.#prevSpecial >= 100 && hero.special < 20) {
      this.#flashes.push({ x: hero.x, y: hero.y, r: 20, life: 0.5, max: 0.5, cor: 0xffd166, anel: true });
      sfx('special');
    }
    this.#prevSpecial = hero.special;
    // assinatura (Cenourada): attackCd saltou p/ cima → pulso em anel
    if (hero.attackCd > this.#prevAtkCd + 1) {
      this.#flashes.push({ x: hero.x, y: hero.y, r: 30, life: 0.22, max: 0.22, cor: 0xff8a3d, anel: true });
    }
    this.#prevAtkCd = hero.attackCd;

    // emite projéteis das armas de longo alcance (orbital/aura são desenhadas direto)
    this.#emitAcc += dtMs;
    if (this.#emitAcc >= 120 && this.#projs.length < 140) {
      this.#emitAcc = 0;
      const alvo = this.#nearest(hero.x, hero.y);
      for (const w of hero.weapons) {
        const def = weaponDef(w.id);
        if (!def || def.mira === 'orbital' || def.mira === 'aura') continue;
        const cor = WEAPON_COR[w.id] ?? 0xff5d8f;
        let dx = 1;
        let dy = 0;
        if (alvo) { dx = alvo.x - hero.x; dy = alvo.y - hero.y; }
        else if (def.mira === 'aleatorio') { dx = Math.random() * 2 - 1; dy = Math.random() * 2 - 1; }
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const sp = def.mira === 'nearest_curto' ? 380 : 560;
        this.#projs.push({
          x: hero.x, y: hero.y, vx: (dx / len) * sp, vy: (dy / len) * sp,
          life: def.mira === 'nearest_curto' ? 0.42 : 0.7, max: 0.7, cor, r: w.evolved ? 6 : 4,
        });
      }
    }
  }

  /** desenha os VFX de arma. */
  #drawVfx(hero: HeroSim | undefined): void {
    const v = this.#vfx;
    v.clear();
    if (hero) {
      // aura de luar sob o herói (glow suave)
      const pulso = 30 + Math.sin(this.#state.tick * 0.1) * 3;
      v.fillStyle(0xbfd7ea, 0.1).fillCircle(hero.x, hero.y + 6, pulso + 8);
      v.fillStyle(0xfff3e0, 0.08).fillCircle(hero.x, hero.y + 6, pulso);
      let oi = 0;
      for (const w of hero.weapons) {
        const def = weaponDef(w.id);
        const cor = WEAPON_COR[w.id] ?? 0xbfd7ea;
        if (def?.mira === 'orbital') {
          const n = 1 + w.level; // mais orbes com o nível
          for (let k = 0; k < n; k++) {
            const a = this.#orbit + (oi * 0.8) + (k / n) * Math.PI * 2;
            v.fillStyle(cor, 1).fillCircle(hero.x + Math.cos(a) * 46, hero.y + Math.sin(a) * 46, w.evolved ? 8 : 6);
          }
          oi += 1;
        } else if (def?.mira === 'aura') {
          const pulso = 60 + Math.sin(this.#orbit * 2) * 6;
          v.lineStyle(3, cor, 0.5).strokeCircle(hero.x, hero.y, pulso);
        }
      }
    }
    for (const p of this.#projs) {
      const a = Math.max(0, p.life / p.max);
      v.fillStyle(p.cor, a).fillCircle(p.x, p.y, p.r);
      v.lineStyle(2, p.cor, a * 0.4).lineBetween(p.x, p.y, p.x - p.vx * 0.02, p.y - p.vy * 0.02); // rastro
    }
    for (const f of this.#flashes) {
      const a = Math.max(0, f.life / f.max);
      if (f.anel) v.lineStyle(3, f.cor, a).strokeCircle(f.x, f.y, f.r);
      else v.fillStyle(f.cor, a).fillCircle(f.x, f.y, f.r * (1.6 - a));
    }
  }

  /** setter de input por toque (analógico virtual do HUD mobile) */
  setTouch(input: HeroInput | null): void {
    this.#touch = input;
  }

  private installTestApi(): void {
    window.__rabbitTest = {
      ...(window.__rabbitTest ?? {}),
      scene: () => 'run',
      fps: () => this.game.loop.actualFps,
      seed: () => this.#config.seed,
      rngSample: window.__rabbitTest?.rngSample ?? (() => []),
      run: () => {
        const s = this.#state;
        const h = s.heroes[0];
        return {
          tick: s.tick, phase: s.phase, hp: h ? Math.ceil(h.hp) : 0,
          level: h?.level ?? 0, luar: s.stats.luar,
          enemies: s.enemies.reduce((n, e) => (e.alive ? n + 1 : n), 0),
          drafting: !!s.pendingDraft,
          checksum: checksumRun(s),
          objetivo: objetivoSnapshot(s),
          heroX: h ? Math.round(h.x) : 0,
          heroY: h ? Math.round(h.y) : 0,
        };
      },
      // AC-M1-07: roda a sim headless NESTE engine e devolve o checksum final (cross-check vs Node).
      // Input gerado pelo MESMO createRng do shared (cross-engine por construção — ver AC-M0-02).
      simChecksum: (seed: number, ticks: number) => runHeadless(seed, ticks),
      // AC-M3-08: completa o objetivo do capítulo NESTE engine (browser) — prova que mineração/escolta
      // funcionam no build do cliente, não só no Node. Determinístico (herói dirigido ao alvo).
      objetivoDemo: (chapter: number) => objetivoHeadless(chapter),
      // AC-M4-06 (a11y): exibe o overlay de draft (DOM mais rico) para o axe-core inspecionar
      demoDraft: () => {
        const cartas = generateDraft(this.#state.heroes[0]!, createRng(1, 3), 3);
        showDraft(cartas, () => {});
      },
      // ACC-4 (reduzir movimento): estado do toggle, tremor gated e modo de inversão de cor
      reducedMotion: () => reducedMotion(),
      demoShake: () => shakeCamera(this),
      inversionMode: () => inversionMode(),
      // AC-M4-04: vence o Norno neste engine (browser) — prova cross-engine da mecânica do boss final
      nornoDemo: () => nornoHeadless(),
      // hook de teste: força a derrota (a morte por cerco é coberta na sim; aqui testamos o FLUXO)
      kill: () => {
        const hero = this.#state.heroes[0];
        if (hero) {
          hero.hp = 0;
          hero.alive = false;
        }
      },
      // hook de teste: avança exatamente N ticks (input nulo) e redesenha — frame determinístico p/ visual
      advance: (n: number) => {
        for (let i = 0; i < n; i++) step(this.#state, this.#config, [{ mx: 0, my: 0 }]);
        this.draw();
      },
    };
  }
}

/** Sim headless com input determinístico — IDÊNTICO no Node e no browser (contrato do AC-M1-07). */
export function runHeadless(seed: number, ticks: number): number {
  const cfg: SimConfig = { seed, chapter: 1, playerCount: 1, arenaW: 1920, arenaH: 1088 };
  const st = createRun(cfg);
  const r = createRng(seed ^ 1, 9);
  for (let t = 0; t < ticks; t++) step(st, cfg, [{ mx: r.next01() * 2 - 1, my: r.next01() * 2 - 1 }]);
  return checksumRun(st);
}

/** AC-M4-04: vence o Norno NESTE engine (browser) — fase 3 + empurrar os 3 ponteiros. Cross-engine. */
export function nornoHeadless(): { fase: number; phase: string; ativados: number } {
  const cfg: SimConfig = { seed: 5, chapter: 12, playerCount: 1, arenaW: 1920, arenaH: 1088, norno: true };
  const st = createRun(cfg);
  const boss = st.enemies.find((e) => e.boss);
  if (boss) boss.hp = boss.maxHp * 0.2; // fases 1–2 são DPS (gameplay); aqui provamos a fase 3 (ponteiros)
  step(st, cfg, [{ mx: 0, my: 0 }]);
  const rumo = (h: { x: number; y: number }, tx: number, ty: number): HeroInput => {
    const dx = tx - h.x;
    const dy = ty - h.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    return { mx: dx / len, my: dy / len };
  };
  for (let t = 0; t < 4000 && st.phase !== 'vitoria' && st.phase !== 'derrota'; t++) {
    const h = st.heroes[0]!;
    const pt = st.norno?.ponteiros.find((p) => !p.ativado);
    const alvo = pt ?? h;
    step(st, cfg, [rumo(h, alvo.x, alvo.y)]);
  }
  return { fase: st.norno?.fase ?? 0, phase: st.phase, ativados: st.norno?.ativados ?? 0 };
}

/** snapshot leve do objetivo do capítulo (AC-M3-08) p/ o E2E. */
function objetivoSnapshot(s: RunState): {
  mineracao?: { feitos: number; meta: number };
  escolta?: { chegou: boolean; npcAlive: boolean };
  completo: boolean;
} | null {
  const o = s.objetivo;
  if (!o) return null;
  return {
    ...(o.mineracao ? { mineracao: { feitos: o.mineracao.feitos, meta: o.mineracao.meta } } : {}),
    ...(o.escolta ? { escolta: { chegou: o.escolta.chegou, npcAlive: o.escolta.npc.alive } } : {}),
    completo: o.completo,
  };
}

/** AC-M3-08: completa o objetivo do capítulo dirigindo o herói ao alvo (mineração→escolta).
 *  Roda NESTE engine (browser no E2E) → prova cross-engine que o objetivo é cumprível. */
export function objetivoHeadless(chapter: number): { phase: string; completo: boolean } {
  const cfg: SimConfig = { seed: 5, chapter, playerCount: 1, arenaW: 1920, arenaH: 1088 };
  const st = createRun(cfg);
  const rumo = (h: { x: number; y: number }, tx: number, ty: number): HeroInput => {
    const dx = tx - h.x;
    const dy = ty - h.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    return { mx: dx / len, my: dy / len };
  };
  for (let t = 0; t < 8000 && st.phase !== 'vitoria' && st.phase !== 'derrota'; t++) {
    const h = st.heroes[0]!;
    const nd = st.objetivo?.mineracao?.nodes.find((n) => !n.mined);
    const alvo = nd ?? st.objetivo?.escolta?.npc ?? h;
    step(st, cfg, [rumo(h, alvo.x, alvo.y)]);
  }
  return { phase: st.phase, completo: st.objetivo?.completo ?? false };
}
